// Shared helpers for AM / PM / SOIRÉE availability slots.
// Single source of truth (must mirror backend constants).

export const SLOT_KEYS = ["am", "pm", "soiree"];

export const SLOT_LABELS = {
  am: "AM",
  pm: "PM",
  soiree: "SOIRÉE",
};

export const DEFAULT_SLOTS = {
  am: { start: "08:00", end: "12:00" },
  pm: { start: "12:00", end: "17:00" },
  soiree: { start: "17:00", end: "23:00" },
};

const isValidSlots = (s) =>
  s && SLOT_KEYS.every((k) => s[k] && typeof s[k].start === "string" && typeof s[k].end === "string");

export { isValidSlots };

/**
 * Resolve effective slots for an event:
 * - event.availability_slots wins if defined
 * - otherwise settings.availability_slots
 * - otherwise DEFAULT_SLOTS
 */
export function resolveSlots(event, settings) {
  if (event && isValidSlots(event.availability_slots)) return event.availability_slots;
  if (settings && isValidSlots(settings.availability_slots)) return settings.availability_slots;
  return DEFAULT_SLOTS;
}

/**
 * Resolve effective slots for a specific date:
 * event.availability_slots_per_date[date] > event.availability_slots > settings > DEFAULT_SLOTS
 */
export function resolveSlotsForDate(event, settings, date) {
  const perDate = event?.availability_slots_per_date;
  if (perDate && isValidSlots(perDate[date])) return perDate[date];
  return resolveSlots(event, settings);
}

const toMin = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string") return null;
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

/**
 * Returns the slot key ("am"|"pm"|"soiree") that a given HH:MM time falls into.
 * Falls back to "am" if no slot matches (defensive).
 */
export function slotForTime(timeStr, slots) {
  const t = toMin(timeStr);
  if (t == null) return null;
  for (const k of SLOT_KEYS) {
    const s = slots?.[k];
    if (!s) continue;
    const start = toMin(s.start);
    const end = toMin(s.end);
    if (start == null || end == null) continue;
    if (t >= start && t < end) return k;
  }
  // Fall back to closest slot by start time
  return SLOT_KEYS[0];
}

/**
 * Extract a slot-level answer from a raw answer value.
 * Legacy: string "yes"|"no" applies to all slots.
 * New: object { am: "yes", pm: "no", soiree: "yes" }.
 */
export function getSlotAnswer(rawAnswer, slot) {
  if (rawAnswer == null) return null;
  if (typeof rawAnswer === "string") return rawAnswer || null;
  if (typeof rawAnswer === "object") {
    const v = rawAnswer[slot];
    return v || null;
  }
  return null;
}

/**
 * Normalize an availability answers map so each date has an object { am, pm, soiree }.
 * Legacy string values become {am: v, pm: v, soiree: v}.
 */
export function normalizeAnswers(answers) {
  if (!answers || typeof answers !== "object") return {};
  const out = {};
  for (const [d, v] of Object.entries(answers)) {
    if (typeof v === "string") {
      out[d] = { am: v, pm: v, soiree: v };
    } else if (v && typeof v === "object") {
      out[d] = {
        am: v.am || null,
        pm: v.pm || null,
        soiree: v.soiree || null,
      };
    } else {
      out[d] = { am: null, pm: null, soiree: null };
    }
  }
  return out;
}

/**
 * For a given game (date + start_time), look up the official's status.
 * Returns "yes" | "no" | null (unknown).
 */
export function availabilityForGame(rawAnswerForDate, game, slots) {
  if (!rawAnswerForDate || !game) return null;
  const slot = slotForTime(game.start_time, slots);
  if (!slot) return null;
  return getSlotAnswer(rawAnswerForDate, slot);
}
